import { useEffect, useState, FormEvent } from 'react';
import { api, setAuthToken } from '../api';
import * as crypto from '../crypto';
import * as recovery from '../recovery_phrase';
import RecoveryPhraseModal from '../components/RecoveryPhraseModal';
import { hasVault, loadVault, clearVault, VaultData } from '../storage';
import { t, useTranslation } from '../i18n';
import * as local from '../messaging/storage_local';

export interface Session {
  /** Username activo ante el server (pseudonym si pseudoOf, real si base). */
  syphraxId: string;
  /** Syphrax_id real del usuario (cuenta base). Igual a syphraxId si es la sesión base. */
  realSyphraxId: string;
  publicKey: Uint8Array;
  privateKey: Uint8Array;
  token: string;
  vault?: VaultData;
  vaultPin?: string;
  /** Argon2id del password. Necesaria para descifrar archive_chunks tras unlock con PIN. */
  wrappingKey?: Uint8Array;
  chatsInitial?: { chatsByFreq?: any; groupsByFreq?: any; contactProfiles?: any };
  pseudoOf?: { freqSecret: string; freqLabel: string };
}

interface Props {
  onAuthed: (session: Session) => void;
}

function humanizeError(err: Error): string {
  const m = err.message;
  if (m.includes('HTTP 409')) return t('auth.error.userExists');
  if (m.includes('HTTP 401')) return t('auth.error.badCreds');
  if (m.includes('HTTP 400')) return t('error.invalid');
  if (m.includes('HTTP 404')) return t('auth.error.userNotFound');
  if (m.includes('HTTP 5')) return t('error.server');
  if (m.toLowerCase().includes('failed to fetch')) return t('auth.error.cantConnect');
  return m;
}

// Inicializa la deviceKey local a partir del password (en este iter, el password
// hace doble función: PIN local + password de cuenta). Después se podrá separar.
async function ensureDeviceKey(password: string): Promise<void> {
  const salt = await local.getOrCreateDeviceSalt();
  const key = await local.deriveDeviceKey(password, salt);
  local.setDeviceKey(key);
}

// Modelo simple (post 2026-05-04): no hay device-identity por dispositivo. La cuenta
// tiene una sola pareja X25519 que vive cifrada en el server. Cualquier dispositivo
// con login + password descifra la priv y descifra todo. No hay registro de device.

export default function Auth({ onAuthed }: Props) {
  useTranslation();
  const [vaultExists, setVaultExists] = useState<boolean | null>(null);
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [recoveryToShow, setRecoveryToShow] = useState<{ phrase: string; session: Session } | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullAuth, setShowFullAuth] = useState(false);
  const [showConfirmForget, setShowConfirmForget] = useState(false);

  useEffect(() => {
    hasVault().then(setVaultExists);
  }, []);

  async function unlock(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await crypto.ready();
      const vault = await loadVault(pin);
      if (!vault) {
        setError(t('auth.error.badPin'));
        setBusy(false);
        return;
      }
      setAuthToken(vault.session.token);
      // Inicializar deviceKey local con el PIN.
      await ensureDeviceKey(pin);
      // CRÍTICO: si el vault persistió wrappingKey, restaurarla en la sesión.
      // Sin esto, archive hydrate retorna early y los salientes del usuario
      // (que solo viven en archive_chunks) desaparecen tras unlock con PIN.
      const wrappingKey = vault.session.wrappingKey
        ? crypto.b64decode(vault.session.wrappingKey)
        : undefined;
      onAuthed({
        syphraxId: vault.session.syphraxId,
        realSyphraxId: vault.session.syphraxId,
        publicKey: crypto.b64decode(vault.session.publicKey),
        privateKey: crypto.b64decode(vault.session.privateKey),
        token: vault.session.token,
        vault,
        vaultPin: pin,
        wrappingKey,
      });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  async function doForgetDevice() {
    setShowConfirmForget(false);
    await clearVault();
    await local.wipeAll();
    setVaultExists(false);
    setPin('');
    setError(null);
  }

  async function handleAuth(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const normalizedUsername = username.trim().toLowerCase();
    try {
      await crypto.ready();
      if (mode === 'register') {
        const kp = crypto.generateKeyPair();
        const salt = crypto.newSalt();
        const wrappingKey = crypto.deriveKeyFromPassword(password, salt);
        const wrapped = crypto.wrapPrivateKey(kp.privateKey, wrappingKey);
        const res = await api.register({
          username: normalizedUsername,
          password,
          public_key: crypto.b64encode(kp.publicKey),
          private_key_encrypted: crypto.b64encode(wrapped.ciphertext),
          private_key_salt: crypto.b64encode(salt),
        });
        setAuthToken(res.token);
        await ensureDeviceKey(password);
        // v112 generar BIP39 recovery phrase y persistir cifrada con wrappingKey local.
        const phrase = recovery.generateRecoveryPhrase();
        try { recovery.persistRecoveryPhrase(phrase, wrappingKey); } catch (_e) { /* localStorage may be off */ }
        const pendingSession: Session = {
          syphraxId: res.syphrax_id,
          realSyphraxId: res.syphrax_id,
          publicKey: kp.publicKey,
          privateKey: kp.privateKey,
          token: res.token,
          wrappingKey,
        };
        setRecoveryToShow({ phrase, session: pendingSession });
        return;
      } else {
        const res = await api.login(normalizedUsername, password);
        const salt = crypto.b64decode(res.private_key_salt!);
        const wrappingKey = crypto.deriveKeyFromPassword(password, salt);
        const privateKey = crypto.unwrapPrivateKey(crypto.b64decode(res.private_key_encrypted!), wrappingKey);
        const publicKey = crypto.b64decode(res.public_key!);
        setAuthToken(res.token);
        await ensureDeviceKey(password);
        onAuthed({
          syphraxId: res.syphrax_id,
          realSyphraxId: res.syphrax_id,
          publicKey,
          privateKey,
          token: res.token,
          wrappingKey,
        });
      }
    } catch (err) {
      setError(humanizeError(err as Error));
    } finally {
      setBusy(false);
    }
  }

  if (vaultExists === null) {
    return <div className="auth"><div className="card"><h1>Syphrax</h1><p className="tagline">{t('auth.loading')}</p></div></div>;
  }

  if (vaultExists && !showFullAuth) {
    return (
      <div className="auth">
        <div className="card">
          <h1>Syphrax</h1>
          <p className="tagline">{t('common.tagline')}</p>
          <p className="hint" style={{ textAlign: 'center', marginBottom: 20 }}>
            {t('auth.savedSession')}
          </p>
          <form onSubmit={unlock}>
            <label>
              <span>{t('auth.field.devicePin')}</span>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                required
                minLength={4}
                disabled={busy}
                autoFocus
              />
            </label>
            <button className="primary" type="submit" disabled={busy || !pin}>
              {busy ? t('auth.btn.unlocking') : t('auth.btn.unlock')}
            </button>
            {error && <div className="error">{error}</div>}
          </form>
          <div className="other-account">
            <button className="ghost" type="button" onClick={() => setShowFullAuth(true)}>
              {t('auth.btn.useOther')}
            </button>
            <button className="ghost danger" type="button" onClick={() => setShowConfirmForget(true)}>
              {t('auth.btn.forget')}
            </button>
          </div>
        </div>
        {showConfirmForget && (
          <div className="modal-backdrop" onClick={() => setShowConfirmForget(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{t('auth.btn.forget')}</h2>
              <p className="hint">{t('auth.confirmForget')}</p>
              <div className="modal-actions">
                <button type="button" className="ghost" onClick={() => setShowConfirmForget(false)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="primary danger" onClick={doForgetDevice}>
                  {t('auth.btn.forget')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (recoveryToShow) {
    return (
      <RecoveryPhraseModal
        phrase={recoveryToShow.phrase}
        mode="show_first_time"
        onClose={() => {
          const s = recoveryToShow.session;
          setRecoveryToShow(null);
          onAuthed(s);
        }}
      />
    );
  }

  return (
    <div className="auth">
      <div className="card">
        <h1>Syphrax</h1>
        <p className="tagline">{t('common.tagline')}</p>
        <div className="tabs">
          <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')} disabled={busy} type="button">{t('auth.tab.login')}</button>
          <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')} disabled={busy} type="button">{t('auth.tab.register')}</button>
        </div>
        <form onSubmit={handleAuth}>
          <label>
            <span>{t('auth.field.username')}</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={64}
              autoCapitalize="none"
              autoCorrect="off"
              disabled={busy}
            />
          </label>
          <label>
            <span>{t('auth.field.password')}</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} disabled={busy} />
          </label>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? t('auth.btn.processing') : mode === 'login' ? t('auth.tab.login') : t('auth.tab.register')}
          </button>
          {error && <div className="error">{error}</div>}
        </form>
        {vaultExists && (
          <div className="other-account">
            <button className="ghost" type="button" onClick={() => setShowFullAuth(false)}>
              {t('auth.btn.backToUnlock')}
            </button>
          </div>
        )}
        <p className="footnote">{t('auth.footnote')}</p>
      </div>
    </div>
  );
}
